import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, Heart, Brain, Utensils, Footprints, MoonStar, Wind, Sprout } from 'lucide-react';
import { PILLAR_LABELS } from '../../lib/pillars';
import { useWorldMode } from '../../lib/worldMode';
import nikkiAvatar from '../../assets/nikki/nikki-sm.png';

/**
 * MobileBottomNav — app-style bottom tab bar (mobile/tablet only; desktop keeps
 * the sidebar). The four wellness pillars flank Nikki in an elevated center
 * button: Move · Recover · ✨Nikki · Mind · Fuel. Labels ALWAYS show so the tab
 * is never ambiguous.
 *
 * The bar follows the dashboard's WORLD MODE:
 *   • Classic — clinical icons; tabs open the numbers-first pillar pages.
 *   • Living  — elemental "world" icons (trail/moon/wind/sprout) glowing in each
 *               world's accent colour; tabs open the immersive /world/:pillar
 *               pages. The whole bar takes on a living-world glow.
 */
const PILLARS = [
  { key: 'movement',  classicIcon: Activity, livingIcon: Footprints, label: PILLAR_LABELS.movement,  classicColor: '#CCFF00', livingColor: '#9BE33B', classic: '/performance', testId: 'bottomnav-movement' },
  { key: 'recovery',  classicIcon: Heart,    livingIcon: MoonStar,   label: PILLAR_LABELS.recovery,  classicColor: '#22d3ee', livingColor: '#5FC6E8', classic: '/recovery',    testId: 'bottomnav-recovery' },
  { key: 'mental',    classicIcon: Brain,    livingIcon: Wind,       label: PILLAR_LABELS.mental,    classicColor: '#a855f7', livingColor: '#A9A7E8', classic: '/mental',      testId: 'bottomnav-mental' },
  { key: 'nutrition', classicIcon: Utensils, livingIcon: Sprout,     label: PILLAR_LABELS.nutrition, classicColor: '#fb923c', livingColor: '#E0B96A', classic: '/nutrition',   testId: 'bottomnav-nutrition' },
];

const Tab = ({ to, icon: Icon, label, color, active, testId }) => (
  <Link
    to={to}
    className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 active:scale-90 transition-transform"
    data-testid={testId}
  >
    <Icon
      className="w-5 h-5 transition-all duration-300"
      strokeWidth={active ? 2.6 : 2}
      style={active
        ? { color, transform: 'scale(1.18)', filter: `drop-shadow(0 0 7px ${color}cc)` }
        : { color: '#a1a1aa' }}
    />
    <span
      className="text-[10px] font-secondary tracking-wide transition-colors"
      style={{ color: active ? '#ffffff' : '#a1a1aa' }}
    >
      {label}
    </span>
  </Link>
);

const MobileBottomNav = () => {
  const { pathname } = useLocation();
  const worldMode = useWorldMode();
  const living = worldMode === 'living';

  const nikkiActive = pathname === '/chat' || pathname === '/';

  // Route, icon, colour + active-match all depend on the world mode.
  // Strip `key` from the spread output — it's our data field, not a React key,
  // and spreading it into <Tab> triggers React's key-in-spread warning.
  const tabFor = (p) => {
    const { key, ...rest } = p;
    return {
      ...rest,
      to: living ? `/world/${key}` : p.classic,
      icon: living ? p.livingIcon : p.classicIcon,
      color: living ? p.livingColor : p.classicColor,
      active: living ? pathname === `/world/${key}` : pathname === p.classic,
    };
  };
  const tabs = PILLARS.map(tabFor);

  return (
    <nav
      className={`lg:hidden fixed bottom-0 inset-x-0 z-40 border-t safe-bottom transition-colors duration-500 ${
        living ? 'lv-bottomnav border-primary/30' : 'hpme-glass border-white/10'
      }`}
      data-testid="mobile-bottom-nav"
      data-mode={worldMode}
    >
      <div className="flex items-end justify-around px-1 h-16">
        <Tab {...tabs[0]} />
        <Tab {...tabs[1]} />

        {/* Center: Nikki — elevated, glowing guide button. Glows brighter in living mode. */}
        <Link to="/chat" className="flex flex-col items-center -mt-6 flex-1 active:scale-95 transition-transform" data-testid="bottomnav-nikki">
          <span className={`w-14 h-14 rounded-full border-2 border-primary overflow-hidden flex items-center justify-center animate-nikki-glow ${living ? 'lv-nikki-living' : 'bg-surface'}`}>
            <img src={nikkiAvatar} alt="Nikki" className="w-full h-full object-cover" />
          </span>
          <span className={`text-[10px] mt-0.5 font-secondary tracking-wide ${nikkiActive ? 'text-primary' : 'text-white'}`}>Nikki</span>
        </Link>

        <Tab {...tabs[2]} />
        <Tab {...tabs[3]} />
      </div>

      {/* Living-mode marker — tucked to the right so it never overlaps Nikki's
          elevated center avatar. */}
      {living && (
        <span className="absolute top-1 right-3 text-[8px] uppercase tracking-[0.18em] text-primary/70 font-secondary font-bold pointer-events-none" data-testid="bottomnav-living-badge">
          Journey
        </span>
      )}
    </nav>
  );
};

export default MobileBottomNav;
